"use client";

import ChatInput from "./ai/ChatInput";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/03-presenters/components/ui/tabs";
import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { useRouter } from "next/navigation";
import { Loader2, Search } from "lucide-react";
import "./SearchBtn.css";
import { useInspirationSearch, useInspirationSearchList, useSubmitInspirationSearch } from "./aiToolV2/inspiration/hooks";
import { countryStore } from "@/03-presenters/components/MultiCountrySelect";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/03-presenters/components/ui/command";
import { CommonSearchItem, HasProductRadio, HasSponsorRadio, PublishDateFilter } from "./aiToolV2/inspiration/InspirationSearch";
import { LoadingOverlay } from "@mantine/core";
import { BrandIcon, CategoryIcon, ProductIcon } from "./aiToolV2/inspiration/icons";
import { InspirationKeywordType } from "./aiToolV2/inspiration/interface";
import { HomeAiChatTab, HomeAiChatProduct, HomeAiChatShop, HomeAiChatVideo, HomeAiChatAuthor, HomeAiChatAds, HomeInspirationTab, HomeSearch, HomeAiChatTemplate, HomeAiChatQuestion } from "@/03-presenters/components/ui/icons";
import { ActiveTemplate } from "./ai/MentionPopup";
import { Editor } from "@tiptap/react";
import { parseInputContent } from "./ai/utils";
import { useGlobalStore, useStore } from "./ai/StoreProvider";
const tabList = [{
  label: "小匠 AI",
  key: "tab1",
  bg: <HomeAiChatTab />
}, {
  label: "卖点可视化",
  key: "tab2",
  bg: <HomeInspirationTab />
}];
const icons = [<HomeAiChatQuestion key="HomeAiChatQuestion" />, <HomeAiChatProduct key="HomeAiChatProduct" />, <HomeAiChatShop key="HomeAiChatShop" />, <HomeAiChatVideo key="HomeAiChatVideo" />, <HomeAiChatAuthor key="HomeAiChatAuthor" />, <HomeAiChatAds key="HomeAiChatAds" />];
const SearchBtnV2 = ({
  firstRowKeywords,
  secondRowKeywords
}: {
  firstRowKeywords: string[];
  secondRowKeywords: string[];
}) => {
  const [activeTab, setActiveTab] = useState("tab1");
  const router = useRouter();
  const {
    countryCode
  } = countryStore();
  const {
    runAsync,
    loading
  } = useSubmitInspirationSearch();
  const {
    keyword,
    setKeyword,
    dropDownLock
  } = useInspirationSearch();
  const [isFocused, setIsFocused] = useState(false);
  const {
    data,
    loading: listSearchLoading,
    debouncedRun: debouncedRunListSearch
  } = useInspirationSearchList();
  const {
    products = [],
    brands = [],
    categories = []
  } = data ?? {};
  const [activeIndex, setActiveIndex] = useState(0);
  const [editor, setEditor] = useState<Editor>();
  const setCurrentEntity = useGlobalStore(state => state.setCurrentEntity);
  const setSearchContent = useGlobalStore(state => state.setSearchContent);
  const store = useStore();
  const slashCommandTemplateList = store(state => state.slashCommandTemplateList);
  const sceneTemplateList = store(state => state.sceneTemplateList);
  const finallyTemplateList = useMemo(() => {
    return [{
      name: "精选问题",
      type: "scene",
      template: sceneTemplateList
    }].concat(slashCommandTemplateList);
  }, [sceneTemplateList, slashCommandTemplateList]);
  const intervalRef = useRef<NodeJS.Timeout>();
  const currentSlashCommand = finallyTemplateList[activeIndex];
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => {
      if (activeIndex < finallyTemplateList.length - 1) {
        setActiveIndex(activeIndex + 1);
      } else {
        setActiveIndex(0);
      }
    }, 5000);
    return () => clearInterval(intervalRef.current);
  }, [activeIndex, finallyTemplateList]);
  return <div data-sentry-component="SearchBtnV2" data-sentry-source-file="SearchBtnV2.tsx">
      <Tabs defaultValue="tab1" onValueChange={value => setActiveTab(value)} data-sentry-element="Tabs" data-sentry-source-file="SearchBtnV2.tsx">
        <TabsList className="h-auto gap-5" data-sentry-element="TabsList" data-sentry-source-file="SearchBtnV2.tsx">
          {tabList.map((item, index) => <TabsTrigger key={item.key} value={item.key} className="group relative !bg-transparent pb-1">
              <div className={cn("absolute -left-[0.5px] top-0 hidden w-[113px]", index !== 0 && "-left-[26px]", activeTab === item.key && "block")}>
                {item.bg}
              </div>

              {index === 0 ? <span className="ml-2 bg-gradient-to-l from-[#DFBFFF] to-[#BFCCFF] bg-clip-text text-base text-transparent">
                  {item.label}
                </span> : <span className="bg-gradient-to-l from-[#DFBFFF] to-[#BFCCFF] bg-clip-text text-base text-transparent">
                  {item.label}
                </span>}
            </TabsTrigger>)}
        </TabsList>

        <TabsContent value="tab1" className="mt-0 h-[300px]" data-sentry-element="TabsContent" data-sentry-source-file="SearchBtnV2.tsx">
          <div className="bottom-0 z-10 min-h-[120px] w-[625px] rounded-md border-[0.5px] border-[#A6A0FF] bg-transparent p-0.5 text-white shadow-[0_0_10px_0_rgba(166,160,255,0.5)_inset] backdrop-blur-lg">
            <ChatInput isAnswering={false} onCancelAnswering={() => {}} onSubmit={content => {
            setSearchContent(content);
            router.push("/ai");
          }} isHome={true} setEditor={setEditor} data-sentry-element="ChatInput" data-sentry-source-file="SearchBtnV2.tsx" />
          </div>

          <div className="mt-6 flex items-center gap-2">
            {finallyTemplateList.map((item, index) => <div key={item.type} className={cn("rounded-full", activeIndex === index && "gradient-border")}>
                <div className={cn("flex items-center gap-2 rounded-full bg-[#201f30] px-5 py-1.5", activeIndex === index && "cursor-pointer bg-[#2a2947]")} onMouseEnter={() => {
              setActiveIndex(index);
            }}>
                  <span className={cn("text-[#84829A]", activeIndex === index && "text-[#B6B1FF]")}>
                    {icons[index]}
                  </span>
                  <span className="text-xs text-[#84829A]">{item.name}</span>
                </div>
              </div>)}
          </div>

          <div className="mt-4 flex flex-col gap-4">
            {currentSlashCommand?.template.map(item => <div className={cn("group flex cursor-pointer items-center gap-2 text-xs text-[#A19FBC] hover:text-[#B6B1FF]")} key={item.title} onClick={() => {
            if (editor) {
              parseInputContent(editor, item.inputContent);
              setCurrentEntity(item.entity ?? "");
            }
          }}>
                <HomeAiChatTemplate />
                <span className="flex items-center gap-1 border-b-1 border-transparent group-hover:border-[#B6B1FF]">
                  {<ActiveTemplate content={item.content} home={true} />}
                </span>
              </div>)}
          </div>
        </TabsContent>

        <TabsContent value="tab2" className="mt-0 h-[300px] w-[625px]" data-sentry-element="TabsContent" data-sentry-source-file="SearchBtnV2.tsx">
          <Command shouldFilter={false} className="border-gradient-to-r relative h-14 w-[625px] overflow-visible rounded-sm border-[0.5px] border-solid border-[#A6A0FF] bg-transparent pl-3 text-white shadow-[0_0_10px_0_rgba(166,160,255,0.5)_inset]" data-sentry-element="Command" data-sentry-source-file="SearchBtnV2.tsx">
            <CommandInput value={keyword} hideSearchIcon disabled={loading} onFocus={() => setIsFocused(true)} onBlur={() => {
            setTimeout(() => {
              if (dropDownLock) return;
              setIsFocused(false);
            }, 500);
          }} inputWrapperClassName="border-none px-0 flex-1" className={cn("h-14 w-full border-none bg-transparent focus-visible:ring-transparent focus-visible:ring-offset-0")} placeholder="输入品牌、品类、产品关键词，检索最新爆款素材" style={{
            outline: "none"
          }} onValueChange={value => {
            setKeyword(value);
            debouncedRunListSearch(value);
          }} data-sentry-element="CommandInput" data-sentry-source-file="SearchBtnV2.tsx" />
            <div className="absolute right-3 top-1/2 z-10 -translate-y-1/2 cursor-pointer border-l border-[#9090914D] pl-3">
              {loading ? <Loader2 className="animate-spin" /> : <Search size={30} />}
            </div>

            <CommandList className={cn("absolute left-0 top-[calc(100%+1rem)] z-50 max-h-[400px] w-full rounded-md bg-white p-4 shadow-[0_0_10px_rgba(0,0,0,0.2)] transition-opacity duration-200", isFocused && keyword.trim() ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0")} data-sentry-element="CommandList" data-sentry-source-file="SearchBtnV2.tsx">
              <div className="flex items-center gap-2">
                <PublishDateFilter data-sentry-element="PublishDateFilter" data-sentry-source-file="SearchBtnV2.tsx" />
                <HasProductRadio data-sentry-element="HasProductRadio" data-sentry-source-file="SearchBtnV2.tsx" />
                <HasSponsorRadio data-sentry-element="HasSponsorRadio" data-sentry-source-file="SearchBtnV2.tsx" />
              </div>
              <CommandGroup className="relative min-h-[60px]" data-sentry-element="CommandGroup" data-sentry-source-file="SearchBtnV2.tsx">
                <LoadingOverlay visible={listSearchLoading} zIndex={11} overlayProps={{
                radius: "xs",
                blur: 2
              }} loaderProps={{
                size: 20
              }} data-sentry-element="LoadingOverlay" data-sentry-source-file="SearchBtnV2.tsx" />
                <div className="mt-4 flex flex-col gap-2">
                  <CommandItem className="group cursor-pointer rounded-md bg-[#F1F1FF] outline outline-1 outline-offset-0 outline-primary" onSelect={async () => {
                  const taskId = await runAsync({
                    keyword
                  });
                  router.push(`/aiToolV2/inspiration/task/${taskId}?countryCode=${countryCode}`);
                }} data-sentry-element="CommandItem" data-sentry-source-file="SearchBtnV2.tsx">
                    <div className="flex w-full items-center gap-1.5">
                      <Search size={14} color="#676A70" data-sentry-element="Search" data-sentry-source-file="SearchBtnV2.tsx" />
                      <span className="font-medium text-[#1C1D1F] text-primary">
                        {`"${keyword}"`}
                      </span>
                    </div>
                  </CommandItem>
                  {brands.length ? <CommonSearchItem data={brands} icon={<BrandIcon />} label="品牌" onSelect={async word => {
                  setKeyword(word);
                  const taskId = await runAsync({
                    type: InspirationKeywordType.BRAND,
                    keyword: word
                  });
                  router.push(`/aiToolV2/inspiration/task/${taskId}?countryCode=${countryCode}`);
                }} /> : null}
                  {categories.length ? <CommonSearchItem data={categories} icon={<CategoryIcon />} label="类目" onSelect={async word => {
                  setKeyword(word);
                  const taskId = await runAsync({
                    type: InspirationKeywordType.CATEGORY,
                    keyword: word
                  });
                  router.push(`/aiToolV2/inspiration/task/${taskId}?countryCode=${countryCode}`);
                }} /> : null}
                  {products?.length ? <CommonSearchItem data={products} icon={<ProductIcon />} label="商品" onSelect={async word => {
                  setKeyword(word);
                  const taskId = await runAsync({
                    type: InspirationKeywordType.PRODUCT,
                    keyword: word
                  });
                  router.push(`/aiToolV2/inspiration/task/${taskId}?countryCode=${countryCode}`);
                }} /> : null}
                </div>
              </CommandGroup>
            </CommandList>
          </Command>

          <div className="shadow-effect mt-6 flex items-center gap-2 overflow-hidden whitespace-nowrap">
            <div className="flex items-center gap-2 [animation:scroll-left_25s_linear_infinite] hover:[animation-play-state:paused]">
              {[...firstRowKeywords, ...firstRowKeywords, ...firstRowKeywords, ...firstRowKeywords].map((item, index) => <div key={index} className="flex items-center gap-2 rounded-full border border-[#C1BFFF66] px-4 py-1 text-sm text-[#84829A] hover:cursor-pointer hover:bg-[#7B7FFF33]" onClick={async () => {
              const taskId = await runAsync({
                keyword: item
              });
              router.push(`/aiToolV2/inspiration/task/${taskId}?countryCode=${countryCode}`);
            }}>
                  <span>{item}</span>
                  <HomeSearch />
                </div>)}
            </div>
          </div>

          <div className="shadow-effect mt-6 flex items-center gap-2 overflow-hidden whitespace-nowrap">
            <div className="flex items-center gap-2 [animation:scroll-right_25s_linear_infinite] hover:[animation-play-state:paused]">
              {[...secondRowKeywords, ...secondRowKeywords, ...secondRowKeywords, ...secondRowKeywords].map((item, index) => <div key={index} className="flex items-center gap-2 rounded-full border border-[#C1BFFF66] px-4 py-1 text-sm text-[#84829A] hover:cursor-pointer hover:bg-[#7B7FFF33]" onClick={async () => {
              const taskId = await runAsync({
                keyword: item
              });
              router.push(`/aiToolV2/inspiration/task/${taskId}?countryCode=${countryCode}`);
            }}>
                  <span>{item}</span>
                  <HomeSearch />
                </div>)}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>;
};
export default SearchBtnV2;